import { useState } from 'react';
import { z } from 'zod';
import { useRepo } from '../app/services';
import { useClubAdmins, useClubMutation, useGroups } from '../data/hooks';
import type { ClubAdminRow, Group, OrgRole } from '../lib/types';
import { fmtAgo } from '../lib/format';
import { ROLE_LABEL } from '../lib/roles';
import { errorText } from '../lib/errors';
import { Badge, Button, Dialog, EmptyState, ErrorState, Field, Loading, Panel, useToast } from './ui';

const inviteSchema = z.object({
  full_name: z.string().trim().min(2, 'Vul de naam in.'),
  email: z.string().trim().email('Vul een geldig e-mailadres in.'),
});

/** "Laatst ingelogd …" of "Uitgenodigd, nog niet ingelogd". */
export function signInLabel(lastSignInAt: string | null): string {
  return lastSignInAt ? `Laatst ingelogd ${fmtAgo(lastSignInAt)}` : 'Uitgenodigd, nog niet ingelogd';
}

/**
 * Club- en groepsbeheerders van één club: lijst, uitnodigen, rol wijzigen en rechten intrekken.
 * Gebruikt door Instellingen (clubbeheerder) en het clubdetail van Antreja (platformbeheerder helpt).
 */
export function ClubAdminsPanel({
  orgId,
  orgName,
  editable,
  currentProfileId,
}: {
  orgId: string;
  orgName: string;
  editable: boolean;
  currentProfileId: string | null;
}) {
  const admins = useClubAdmins(orgId);
  const groups = useGroups(orgId);
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<ClubAdminRow | null>(null);
  const repo = useRepo();
  const toast = useToast();
  const setRole = useClubMutation(orgId, (v: { profileId: string; role: OrgRole }) => repo.setMemberRole(orgId, v.profileId, v.role));
  const resend = useClubMutation(orgId, (profileId: string) => repo.resendClubAdminInvite(orgId, profileId));

  const rows = admins.data ?? [];
  const clubAdminCount = rows.filter((r) => r.role === 'club_admin').length;
  const groupName = new Map((groups.data ?? []).map((g) => [g.id, g.name]));

  return (
    <Panel
      title="Beheerders"
      description="Clubbeheerders regelen alles in het portaal; groepsbeheerders alleen hun eigen groepen. Een club houdt altijd minstens één clubbeheerder."
      actions={
        editable && (
          <Button variant="primary" size="sm" style={{ whiteSpace: 'nowrap' }} onClick={() => setInviting(true)}>
            Beheerder uitnodigen
          </Button>
        )
      }
    >
      {admins.isLoading ? (
        <Loading />
      ) : admins.error ? (
        <ErrorState error={admins.error} />
      ) : rows.length === 0 ? (
        <EmptyState title="Nog geen beheerders">Nodig de contactpersoon van de club uit als clubbeheerder.</EmptyState>
      ) : (
        <ul className="tile-list">
          {rows.map((a) => {
            const lastAdmin = a.role === 'club_admin' && clubAdminCount <= 1;
            const isMe = a.profile_id === currentProfileId;
            return (
              <li key={a.profile_id} style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <span style={{ minWidth: 0 }}>
                  <span className="primary">
                    {a.full_name}
                    {isMe && <span className="muted small"> (jij)</span>}
                  </span>
                  {a.email && <span className="cell-sub">{a.email}</span>}
                  <span className="cell-sub row" style={{ gap: 6, marginTop: 4 }}>
                    {a.last_sign_in_at ? signInLabel(a.last_sign_in_at) : <Badge tone="yellow">{signInLabel(null)}</Badge>}
                    {a.role === 'club_admin' && a.last_sign_in_at && a.totp_enabled === false && (
                      <Badge tone="yellow">Nog geen tweestapsverificatie</Badge>
                    )}
                  </span>
                  {a.role === 'group_manager' && (
                    <span className="cell-sub">
                      {a.managed_group_ids.length
                        ? `Beheert: ${a.managed_group_ids.map((g) => groupName.get(g) ?? 'onbekende groep').join(', ')}`
                        : 'Beheert nog geen groep'}
                    </span>
                  )}
                </span>
                {editable ? (
                  <span className="row" style={{ gap: 8 }}>
                    <label className="sr-only" htmlFor={`role-${a.profile_id}`}>
                      Rol van {a.full_name}
                    </label>
                    <select
                      id={`role-${a.profile_id}`}
                      className="input"
                      style={{ width: 'auto', minHeight: 36 }}
                      value={a.role}
                      disabled={setRole.isPending || lastAdmin}
                      title={lastAdmin ? 'De laatste clubbeheerder kan geen andere rol krijgen.' : undefined}
                      onChange={async (e) => {
                        try {
                          await setRole.mutateAsync({ profileId: a.profile_id, role: e.target.value as OrgRole });
                          toast(`${a.full_name} is nu ${ROLE_LABEL[e.target.value as 'club_admin' | 'group_manager'].toLowerCase()}`);
                        } catch (err) {
                          toast(errorText(err, 'Rol wijzigen mislukt'), 'error');
                        }
                      }}
                    >
                      <option value="club_admin">Clubbeheerder</option>
                      <option value="group_manager">Groepsbeheerder</option>
                    </select>
                    {!a.last_sign_in_at && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={resend.isPending}
                        onClick={async () => {
                          try {
                            const r = await resend.mutateAsync(a.profile_id);
                            toast(`Uitnodiging opnieuw verstuurd naar ${r.email}`);
                          } catch (err) {
                            toast(errorText(err, 'Opnieuw versturen mislukt'), 'error');
                          }
                        }}
                      >
                        Uitnodiging opnieuw sturen
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" disabled={lastAdmin} title={lastAdmin ? 'Nodig eerst een andere clubbeheerder uit.' : undefined} onClick={() => setRemoving(a)}>
                      Rechten intrekken
                    </Button>
                  </span>
                ) : (
                  <Badge tone={a.role === 'club_admin' ? 'ink' : 'neutral'}>{ROLE_LABEL[a.role as 'club_admin' | 'group_manager']}</Badge>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {inviting && <InviteAdminDialog orgId={orgId} orgName={orgName} groups={groups.data ?? []} onClose={() => setInviting(false)} />}
      {removing && <RemoveAdminDialog orgId={orgId} admin={removing} isMe={removing.profile_id === currentProfileId} onClose={() => setRemoving(null)} />}
    </Panel>
  );
}

function RemoveAdminDialog({ orgId, admin, isMe, onClose }: { orgId: string; admin: ClubAdminRow; isMe: boolean; onClose: () => void }) {
  const repo = useRepo();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const remove = useClubMutation(orgId, () => repo.removeClubRole(orgId, admin.profile_id));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Beheerrechten van ${admin.full_name} intrekken?`}
      description={
        isMe
          ? 'Je haalt je eigen beheerrechten weg. Daarna kun je dit portaal voor deze club niet meer gebruiken.'
          : 'Deze persoon kan daarna niet meer in het portaal. Toegang tot deuren via de app blijft zoals die is.'
      }
    >
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      <div className="dialog-actions">
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={async () => {
            try {
              await remove.mutateAsync(undefined);
              toast(`Beheerrechten van ${admin.full_name} ingetrokken`);
              onClose();
            } catch (err) {
              setError(errorText(err, 'Intrekken mislukt'));
            }
          }}
        >
          Rechten intrekken
        </Button>
      </div>
    </Dialog>
  );
}

export function InviteAdminDialog({
  orgId,
  orgName,
  groups,
  onClose,
}: {
  orgId: string;
  orgName: string;
  groups: Group[];
  onClose: () => void;
}) {
  const repo = useRepo();
  const toast = useToast();
  const [form, setForm] = useState({ full_name: '', email: '' });
  const [role, setRole] = useState<'club_admin' | 'group_manager'>('club_admin');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const invite = useClubMutation(orgId, (v: Parameters<typeof repo.inviteClubAdmin>[0]) => repo.inviteClubAdmin(v));
  const bind = (k: keyof typeof form) => ({
    className: 'input',
    value: form[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Beheerder uitnodigen"
      description={`De uitgenodigde krijgt een e-mail om in te loggen op het portaal van ${orgName}. Een clubbeheerder stelt bij de eerste keer inloggen tweestapsverificatie in.`}
    >
      <form
        className="stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const p = inviteSchema.safeParse(form);
          const errs: Record<string, string> = {};
          if (!p.success) for (const i of p.error.issues) errs[String(i.path[0])] = i.message;
          setErrors(errs);
          if (!p.success) return;
          try {
            const r = await invite.mutateAsync({
              organization_id: orgId,
              email: p.data.email.toLowerCase(),
              full_name: p.data.full_name,
              role,
              group_ids: role === 'group_manager' ? groupIds : [],
            });
            toast(
              r.status === 'existing_user'
                ? `${p.data.full_name} had al een account en kan direct inloggen`
                : `Uitnodiging verstuurd naar ${p.data.email.toLowerCase()}`,
            );
            onClose();
          } catch (err) {
            setErrors({ form: errorText(err, 'Uitnodigen mislukt') });
          }
        }}
      >
        <Field label="Naam" error={errors.full_name}>
          {(p) => <input {...p} {...bind('full_name')} autoComplete="off" />}
        </Field>
        <Field label="E-mailadres" error={errors.email}>
          {(p) => <input {...p} {...bind('email')} type="email" autoComplete="off" />}
        </Field>
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 8 }}>
          <legend className="label" style={{ marginBottom: 8 }}>
            Rol
          </legend>
          <div className="choice-group">
            <label className="choice">
              <input type="radio" name="admin-role" checked={role === 'club_admin'} onChange={() => setRole('club_admin')} />
              <span>
                <strong>Clubbeheerder</strong>
                <span>Regelt alles: deuren, groepen, personen, codes en beheerders.</span>
              </span>
            </label>
            <label className="choice">
              <input type="radio" name="admin-role" checked={role === 'group_manager'} onChange={() => setRole('group_manager')} />
              <span>
                <strong>Groepsbeheerder</strong>
                <span>Beheert alleen de leden en codes van eigen groepen, bijvoorbeeld een teammanager.</span>
              </span>
            </label>
          </div>
        </fieldset>
        {role === 'group_manager' && (
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 6 }}>
            <legend className="label" style={{ marginBottom: 6 }}>
              Welke groepen beheert deze persoon?
            </legend>
            {groups.length === 0 ? (
              <p className="muted small">Er zijn nog geen groepen. Je kunt groepen later toewijzen.</p>
            ) : (
              groups.map((g) => (
                <label key={g.id} className="row" style={{ gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={groupIds.includes(g.id)}
                    onChange={(e) => setGroupIds((ids) => (e.target.checked ? [...ids, g.id] : ids.filter((x) => x !== g.id)))}
                  />
                  {g.name}
                </label>
              ))
            )}
          </fieldset>
        )}
        {errors.form && (
          <div className="alert alert-danger" role="alert">
            {errors.form}
          </div>
        )}
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={invite.isPending}>
            Uitnodiging versturen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
